import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../api/axiosClient";
import {
  KPIGrid, DataTable, Btn, Alert, Tabs,
  FilterPills, StatusBadge, StandardModal,
  colors, inputStyle, selectStyle, textareaStyle,
} from "../design-system";

const TABS = [
  { key: "roster",       label: "Roster" },
  { key: "compliance",   label: "Compliance" },
  { key: "declarations", label: "Declarations" },
];

const TYPE_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "trainer", label: "Trainers" },
  { value: "assessor", label: "Assessors" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "suspended", label: "Suspended" },
  { value: "revoked", label: "Revoked" },
  { value: "lapsed", label: "Lapsed" },
  { value: "reapproval_due", label: "Re-approval Due" },
];

const DECL_STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "submitted", label: "Submitted" },
  { value: "reviewed_no_action", label: "Reviewed — No Action" },
  { value: "reviewed_recusal_required", label: "Reviewed — Recusal Required" },
  { value: "reviewed_managed", label: "Reviewed — Managed" },
];

const emptyCreateForm = {
  person_type: "trainer", first_name: "", last_name: "", email: "",
  job_title: "", teaching_qualification: "", assessing_qualification: "",
  occupational_sector: "", years_experience: "", notes: "",
};

const emptyDeclForm = {
  declarant_name: "", declarant_role: "", declarant_email: "",
  has_conflict: false, conflict_description: "", providers_affected: "",
};

export default function TrainerAssessors() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("roster");
  const [people, setPeople] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [declStatusFilter, setDeclStatusFilter] = useState("");
  const [msg, setMsg] = useState(null);
  const [msgType, setMsgType] = useState("success");

  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [saving, setSaving] = useState(false);

  const [declModal, setDeclModal] = useState(false);
  const [declForm, setDeclForm] = useState(emptyDeclForm);
  const [savingDecl, setSavingDecl] = useState(false);

  const flash = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(null), 4000); };

  const loadRoster = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.person_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await axiosClient.get("/trainer-assessors", { params });
      setPeople(res.data);
    } catch (e) {
      flash(e.userMessage || "Failed to load roster", "error");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  const loadCompliance = useCallback(async () => {
    try {
      const res = await axiosClient.get("/trainer-assessors/compliance-summary");
      setCompliance(res.data);
    } catch (e) {
      flash(e.userMessage || "Failed to load compliance summary", "error");
    }
  }, []);

  const loadDeclarations = useCallback(async () => {
    try {
      const params = {};
      if (declStatusFilter) params.status = declStatusFilter;
      const res = await axiosClient.get("/trainer-assessors/declarations", { params });
      setDeclarations(res.data);
    } catch (e) {
      flash(e.userMessage || "Failed to load declarations", "error");
    }
  }, [declStatusFilter]);

  useEffect(() => { loadRoster(); }, [loadRoster]);
  useEffect(() => { if (tab === "compliance") loadCompliance(); }, [tab, loadCompliance]);
  useEffect(() => { if (tab === "declarations") loadDeclarations(); }, [tab, loadDeclarations]);

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...createForm };
      if (payload.years_experience === "") delete payload.years_experience;
      else payload.years_experience = parseInt(payload.years_experience, 10);
      const res = await axiosClient.post("/trainer-assessors", payload);
      flash(`${res.data.full_name} added — pending approval.`);
      setCreateModal(false);
      setCreateForm(emptyCreateForm);
      loadRoster();
      navigate(`/accreditation/trainer-assessors/${res.data.id}`);
    } catch (e) {
      flash(e.userMessage || "Failed to create record", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateDeclaration(e) {
    e.preventDefault();
    setSavingDecl(true);
    try {
      await axiosClient.post("/trainer-assessors/declarations", declForm);
      flash("Declaration submitted.");
      setDeclModal(false);
      setDeclForm(emptyDeclForm);
      loadDeclarations();
    } catch (e) {
      flash(e.userMessage || "Failed to submit declaration", "error");
    } finally {
      setSavingDecl(false);
    }
  }

  const complianceKpis = compliance ? [
    { label: "Re-approvals Due", value: compliance.reapproval?.due_count ?? compliance.reapproval?.length ?? 0, color: "orange", icon: "🔄" },
    { label: "Rotation Flags", value: compliance.rotation?.flagged_count ?? compliance.rotation?.length ?? 0, color: "amber", icon: "🔁" },
    { label: "CPD Shortfalls", value: compliance.cpd?.shortfall_count ?? compliance.cpd?.length ?? 0, color: "red", icon: "📉" },
    { label: "Declaration Reminders", value: compliance.declarations?.due_count ?? compliance.declarations?.length ?? 0, color: "blue", icon: "📢" },
  ] : [];

  const rosterColumns = [
    { key: "full_name", label: "Name", render: (_, r) => (
      <div>
        <div style={{ fontWeight: 600 }}>{r.full_name}</div>
        <div style={{ fontSize: 12, color: colors.textMuted }}>{r.email || "—"}</div>
      </div>
    )},
    { key: "person_type", label: "Type", render: (_, r) => (
      <span style={{ textTransform: "capitalize" }}>{r.person_type}</span>
    )},
    { key: "status", label: "Status", render: (_, r) => <StatusBadge status={r.status} /> },
    { key: "cpd", label: "CPD", render: (_, r) => (
      r.cpd_year_target ? (
        <span style={{ fontSize: 13 }}>
          {r.cpd_year_completed || 0} / {r.cpd_year_target} hrs
          {r.cpd_percent != null && (
            <span style={{ color: r.cpd_percent >= 100 ? colors.success : colors.textMuted }}> ({r.cpd_percent}%)</span>
          )}
        </span>
      ) : "—"
    )},
    { key: "approval_expiry", label: "Expires", render: (_, r) => {
      if (!r.approval_expiry) return "—";
      const soon = r.days_to_expiry != null && r.days_to_expiry <= 90;
      return <span style={{ color: soon ? colors.warning : "inherit" }}>{new Date(r.approval_expiry).toLocaleDateString("en-GB")}</span>;
    }},
    { key: "actions", label: "", render: (_, r) => (
      <Btn size="sm" variant="ghost" onClick={() => navigate(`/accreditation/trainer-assessors/${r.id}`)}>View</Btn>
    )},
  ];

  const declColumns = [
    { key: "declarant_name", label: "Declarant", render: (_, r) => (
      <div>
        <div style={{ fontWeight: 600 }}>{r.declarant_name}</div>
        <div style={{ fontSize: 12, color: colors.textMuted }}>{r.declarant_role || r.declarant_email || "—"}</div>
      </div>
    )},
    { key: "declaration_year", label: "Year" },
    { key: "has_conflict", label: "Conflict?", render: (_, r) => (
      <StatusBadge status={r.has_conflict ? "pending" : "active"} customLabel={r.has_conflict ? "Declared" : "None"} />
    )},
    { key: "status", label: "Review Status", render: (_, r) => <StatusBadge status={r.status} customLabel={r.status.replace(/_/g, " ")} /> },
    { key: "submitted_at", label: "Submitted", render: (_, r) => r.submitted_at ? new Date(r.submitted_at).toLocaleDateString("en-GB") : "—" },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>Trainers &amp; Assessors</h1>
          <p style={{ color: colors.textMuted, margin: "4px 0 0" }}>Approval lifecycle, CPD compliance, and conflict declarations</p>
        </div>
        {tab === "roster" && <Btn variant="primary" onClick={() => setCreateModal(true)}>+ New Trainer / Assessor</Btn>}
        {tab === "declarations" && <Btn variant="primary" onClick={() => setDeclModal(true)}>+ New Declaration</Btn>}
      </div>

      {msg && <Alert type={msgType} message={msg} style={{ marginBottom: 16 }} />}

      <Tabs tabs={TABS} activeTab={tab} onChange={setTab} style={{ marginBottom: 24 }} />

      {tab === "roster" && (
        <div>
          <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
            <FilterPills options={TYPE_FILTER_OPTIONS} value={typeFilter} onChange={setTypeFilter} />
            <FilterPills options={STATUS_FILTER_OPTIONS} value={statusFilter} onChange={setStatusFilter} />
          </div>
          <DataTable columns={rosterColumns} rows={people} keyField="id" loading={loading} emptyMessage="No trainers or assessors found." />
        </div>
      )}

      {tab === "compliance" && (
        <div>
          {!compliance ? (
            <p style={{ color: colors.textMuted }}>Loading compliance summary…</p>
          ) : (
            <KPIGrid kpis={complianceKpis} columns={4} />
          )}
        </div>
      )}

      {tab === "declarations" && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <FilterPills options={DECL_STATUS_OPTIONS} value={declStatusFilter} onChange={setDeclStatusFilter} />
          </div>
          <DataTable columns={declColumns} rows={declarations} keyField="id" loading={loading} emptyMessage="No declarations found." />
        </div>
      )}

      {createModal && (
        <StandardModal
          title="New Trainer / Assessor"
          onClose={() => setCreateModal(false)}
          width={640}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setCreateModal(false)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="new-ta-form" disabled={saving}>
                {saving ? "Creating…" : "Create"}
              </Btn>
            </div>
          }
        >
          <form id="new-ta-form" onSubmit={handleCreate}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Type *</label>
                <select required value={createForm.person_type} onChange={e => setCreateForm(f => ({ ...f, person_type: e.target.value }))} style={selectStyle}>
                  <option value="trainer">Trainer</option>
                  <option value="assessor">Assessor</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Email</label>
                <input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>First Name *</label>
                <input required value={createForm.first_name} onChange={e => setCreateForm(f => ({ ...f, first_name: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Last Name *</label>
                <input required value={createForm.last_name} onChange={e => setCreateForm(f => ({ ...f, last_name: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Job Title</label>
                <input value={createForm.job_title} onChange={e => setCreateForm(f => ({ ...f, job_title: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Years Experience</label>
                <input type="number" min="0" value={createForm.years_experience} onChange={e => setCreateForm(f => ({ ...f, years_experience: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Teaching Qualification</label>
                <input value={createForm.teaching_qualification} onChange={e => setCreateForm(f => ({ ...f, teaching_qualification: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Assessing Qualification</label>
                <input value={createForm.assessing_qualification} onChange={e => setCreateForm(f => ({ ...f, assessing_qualification: e.target.value }))} style={inputStyle} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Occupational Sector</label>
                <input value={createForm.occupational_sector} onChange={e => setCreateForm(f => ({ ...f, occupational_sector: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Notes</label>
              <textarea rows={3} value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))} style={textareaStyle} />
            </div>
          </form>
        </StandardModal>
      )}

      {declModal && (
        <StandardModal
          title="New Conflict of Interest Declaration"
          onClose={() => setDeclModal(false)}
          width={560}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setDeclModal(false)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="new-decl-form" disabled={savingDecl}>
                {savingDecl ? "Submitting…" : "Submit"}
              </Btn>
            </div>
          }
        >
          <form id="new-decl-form" onSubmit={handleCreateDeclaration}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Declarant Name *</label>
                <input required value={declForm.declarant_name} onChange={e => setDeclForm(f => ({ ...f, declarant_name: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Role</label>
                <input value={declForm.declarant_role} onChange={e => setDeclForm(f => ({ ...f, declarant_role: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginTop: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={declForm.has_conflict} onChange={e => setDeclForm(f => ({ ...f, has_conflict: e.target.checked }))} />
              I am declaring a conflict of interest
            </label>
            {declForm.has_conflict && (
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Conflict Description *</label>
                <textarea required rows={3} value={declForm.conflict_description} onChange={e => setDeclForm(f => ({ ...f, conflict_description: e.target.value }))} style={textareaStyle} />
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", margin: "12px 0 4px" }}>Providers Affected</label>
                <input value={declForm.providers_affected} onChange={e => setDeclForm(f => ({ ...f, providers_affected: e.target.value }))} style={inputStyle} placeholder="Comma-separated provider names" />
              </div>
            )}
          </form>
        </StandardModal>
      )}
    </div>
  );
}
